import type * as React from 'react'
import { useEffect } from 'react'

import {
  Button,
  DropdownMenu,
  Text,
  Tooltip,
  ChevronDownIcon,
  SettingsIcon,
  Flex,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import { selectCurrentOrganizationId } from '~publish/legacy/organizations/selectors'
import { useAppSelector } from '~publish/legacy/store'

import { useRemindersToggle } from '../../../reminders-bar/hooks'
import { RemindersIcon } from '../../../RemindersIcon'

import styles from './Select.module.css'

const handleSchedulingTypeChange = ({
  draft,
  schedulingType,
  toggleReminder,
  organizationId,
}: {
  draft: Draft
  schedulingType: string
  toggleReminder: (isReminder: boolean) => void
  organizationId: string
}): void => {
  const isReminder = schedulingType === 'notifyMe'
  toggleReminder(isReminder)

  BufferTracker.schedulingTypeChanged({
    organizationId,
    channel: draft.service.name,
    clientName: Client.PublishWeb,
    schedulingType: isReminder ? 'notification' : 'automatic',
    updateType: draft.updateType ?? 'post',
  })
}

export const Select = ({
  draft,
  disabled,
  disableOptionDescription,
  isAutomaticPostingPossible,
  id,
}: {
  draft: Draft
  disabled?: boolean | undefined
  id: string
  disableOptionDescription: string
  isAutomaticPostingPossible: boolean
}): React.JSX.Element => {
  const organizationId = useAppSelector((state) =>
    selectCurrentOrganizationId(state),
  )

  const { isOn, toggleReminder } = useRemindersToggle(draft)

  useEffect(() => {
    if (!isAutomaticPostingPossible && !draft.isReminder) {
      handleSchedulingTypeChange({
        draft,
        schedulingType: 'notifyMe',
        toggleReminder,
        organizationId,
      })
    }
  }, [
    draft,
    isAutomaticPostingPossible,
    draft.isReminder,
    toggleReminder,
    organizationId,
  ])

  return (
    <DropdownMenu disabled={disabled}>
      <Tooltip
        content={
          draft.isReminder || !isAutomaticPostingPossible
            ? "You'll receive a mobile notification to post yourself"
            : 'Buffer will post automatically for you'
        }
        align="end"
      >
        <DropdownMenu.Trigger asChild>
          <Button
            id={id}
            aria-label="reminders"
            disabled={disabled}
            variant="tertiary"
          >
            {draft.isReminder || !isAutomaticPostingPossible ? (
              <RemindersIcon />
            ) : (
              <SettingsIcon color="brand" />
            )}
            {draft.isReminder || !isAutomaticPostingPossible
              ? 'Notify Me'
              : 'Automatic'}
            <ChevronDownIcon />
          </Button>
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Content align="end" className={styles.dropdownContent}>
        <DropdownMenu.RadioGroup
          value={isOn ? 'notifyMe' : 'automatic'}
          onChange={(value): void =>
            handleSchedulingTypeChange({
              draft,
              schedulingType: value,
              toggleReminder,
              organizationId,
            })
          }
        >
          <TooltipWrapper
            content={
              !isAutomaticPostingPossible ? disableOptionDescription : ''
            }
          >
            <DropdownMenu.RadioItem
              value="automatic"
              disabled={!isAutomaticPostingPossible}
            >
              <Flex direction="column" gap="xs">
                <Flex align="center" gap="xs">
                  <SettingsIcon color="brand" />
                  <Text weight="medium">Automatic</Text>
                </Flex>
                <Text color="subtle" className={styles.description}>
                  Buffer will post automatically for you
                </Text>
              </Flex>
            </DropdownMenu.RadioItem>
          </TooltipWrapper>
          <DropdownMenu.RadioItem value="notifyMe">
            <Flex direction="column" gap="xs">
              <Flex align="center" gap="xs">
                <RemindersIcon />
                <Text weight="medium">Notify Me</Text>
              </Flex>
              <Text color="subtle" className={styles.description}>
                You'll receive a mobile notification to post yourself.
              </Text>
            </Flex>
          </DropdownMenu.RadioItem>
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

const TooltipWrapper = ({
  children,
  content,
}: {
  children: React.ReactNode
  content: string
}): React.JSX.Element => {
  if (!content) {
    return <>{children}</>
  }
  return <Tooltip content={content}>{children}</Tooltip>
}
